import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from "react-redux";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/loaders/Loader";
import PreTableManageBlogs from "../../components/tables/PreTableManageBlogs";
import CustomSelect from "../../components/CustomSelect";
import {USER_PROJECT, USER_ROLE} from "../../constants/Auth";
import CustomTooltip from "../../components/CustomTooltip";
import {logOut} from "../../redux/dataUploader/dataSlice";

const ManageBlogs = () => {

  const dispatch = useDispatch();
  const token = localStorage.getItem("auth_token");
  const navigate = useNavigate();
  const role = localStorage.getItem(USER_ROLE);

  const [blogsList, setBlogsList] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [usersList, setUsersList] = useState({});
  const [selectUsersList, setSelectUsersList] = useState([]);
  const [manageCompaniesIdList, setManageCompaniesIdList] = useState({});
  const [columnDefs, setColumnDefs] = useState([]);
  const [isPopupType, setPopupType] = useState('updating');
  const [addPopupStatus, setAddPopupStatus] = useState(false);
  const [createPopupStatus, setCreatePopupStatus] = useState(false);
  const [deletePopupStatus, setDeletePopupStatus] = useState(false);
  const [isTriggerUpdate, setIsTriggerUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addPopupWithConfigData] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [reload, setReload] = useState(0);

  const [selectedUser, setSelectedUser] = useState({
      id: localStorage.getItem("id"),
      name: localStorage.getItem("name"),
  })

  const selectStyles = {
    container: (baseStyles) => ({
        ...baseStyles,
        maxWidth: "180px",
        width: "100%",
        margin: "0 20px 20px 0",
    }),
    control: (baseStyles, state) => ({
        ...baseStyles,
        fontSize: "14px",
        lineHeight: "1.2",
        minHeight: "26px",
        border: "none",
        borderRadius: "8px",
        "&:hover": {
            cursor: "pointer",
        },
    }),
    placeholder: (baseStyles) => ({
        ...baseStyles,
        fontWeight: 500,
        color: "#1A3353",
    }),
    menu: (baseStyles, state) => ({
        ...baseStyles,
        zIndex: "6",
        "&:hover": {
            cursor: "pointer",
        },
    }),
    option: (baseStyles, state) => ({
        ...baseStyles,
        fontSize: "14px",
        lineHeight: "1.2",
        "&:hover": {
            cursor: "pointer",
        },
    }),
  } 

  const checkSession = (status) => {
      if(status === 401) {
          dispatch(logOut());
      }
  }

  useEffect(() => {
      if(reload) {
          getAllBlogs(selectedUser.id);
      }
  }, [reload])

  /* fetch user services */
  useEffect(() => {
      setIsLoading(true);

      // initial fetch
      fetch(`${localStorage.getItem("apiUrl")}api/user/${selectedUser.id}/services?status=true&is_archived=false`, {
          headers: {
              "Accept": "application/json",
              "Content-Type": "application/json",
              'Authorization': `Bearer ${token}`
          },
      })
          .then(res => {
            checkSession(res.status);

            if (res.ok) {
                return res.json()
            }
            return Promise.reject(res)
          })
          .then(res => {
            setSelectedUser({id: res.id, name: res.name})
                
            if(res["user_managers_accounts"].length === 0) {
              handleChangeSelectedUser({value: res.id, name: res.name})
              setSelectUsersList([{id: res.id, name: res.name}])
            } else {
              setSelectUsersList([...res["user_managers_accounts"] || []])
              setIsLoading(false)
            }
          })
          .catch(res => {
            setIsLoading(false)
            console.log("Something went wrong")
          })
  }, []);

  const handleChangeSelectedUser = (user) => {
    setIsLoading(true);

    // fetch user services
    fetch(`${localStorage.getItem("apiUrl")}api/user/${user.value}/services`, {
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        },
    })
        .then(res => {

            checkSession(res.status);

            if (res.ok) {
                return res.json()
            }
            return Promise.reject(res)
        })
        .then(res => {
            setSelectedUser({
                id: res.id,
                name: res.name
            })
            getAllBlogs(res.id);
            
        })
        .catch(res => {
            setIsLoading(false);
            console.error('Error');
        })  
    }

  // Get blogs
  const getAllBlogs = async (id) => {
    try {
      setIsLoading(true);

      const res = await fetch(`${localStorage.getItem("apiUrl")}api/blogs/${id}?per_page=1000`, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      });

      checkSession(res.status);
      if (!res.ok) throw new Error("Failed to fetch blogs");
      
      const data = await res.json();
      setBlogsList(data?.blogs?.data || []);

      await getAllProjects(id);
    } catch (error) {
      console.error("Server connection error:", error);
    } finally {
      setIsLoading(false);
    } 
  };

  const toIdNameArray = (obj) => {
    if (!obj || typeof obj !== 'object') return [];

    const normalize = (s) =>
      String(s ?? '')
        .replace(/\s+/g, ' ')
        .trim();

    return Object.entries(obj)
      .map(([k, v]) => ({
        id: Number(k),
        name: normalize(v)
      }))
      .filter(item => Number.isFinite(item.id) && item.name.length > 0)
      .sort((a, b) => a.id - b.id);
  }
  
  // Get projects
  const getAllProjects = async (id) => {
    try {
      setIsLoading(true);

      const res = await fetch(`${localStorage.getItem("apiUrl")}api/managing-blogs-and-companies?user_id=${id}`, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      });

      checkSession(res.status);
      if (!res.ok) throw new Error("Failed to fetch companies and blogs");

      const data = await res.json();
      
      setProjectsList(toIdNameArray(data?.projects) || []);
      
      const oneUser = {}
      if(!data?.users) {
        oneUser[`${selectedUser.id}`] = `${selectedUser.name}`;
      }
      setUsersList(data?.users || oneUser);
      
      setManageCompaniesIdList(data?.companies || []);
    } catch (error) {
      console.error("Server connection error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect(() => {
  //   getAllBlogs();
  // }, [])

  useEffect(() => {
    setBlogsList(prev =>
      prev.map(c => ({
        ...c,
        userName: usersList?.[c.user_id] ?? null
      }))
    );
    
  }, [usersList])

  useEffect(() => {
    setBlogsList(prev =>
      prev.map(c => ({
        ...c,
        manageCompany: manageCompaniesIdList?.[c.manage_companies_id] ?? null
      }))
    );
    
  }, [manageCompaniesIdList])

  useEffect(() => {
    async function update() {
        if (isTriggerUpdate) {
          getAllBlogs(selectedUser.id)
            return setIsTriggerUpdate(false)
        }
    }

    update()
  }, [isTriggerUpdate]);

  const modifiedColumnsData = (columns) => columns.map(el => {
      let columnDef = {
          field: el,
          filter: true,
          wrapText: true,
          tooltipField: el,
          tooltipComponent: CustomTooltip,
      };

      if (el === 'id') {
          columnDef = {
              ...columnDef,
              minWidth: 20,
              maxWidth: 80,
              hide: true
          }
      } else if (el === 'domain') {
          columnDef = {
              ...columnDef,
              minWidth: 200,
              maxWidth: 400 
          }
      } else if (el === 'short_name') {
          columnDef = {
              ...columnDef,
              minWidth: 100,
              maxWidth: 330 
          }
      } else if (el === 'userName' && role === 'admin') {
          columnDef = {
              ...columnDef,
              minWidth: 300,
              maxWidth: 360 
          }
      } else if (el === 'userName' && role !== 'admin') {
          columnDef = {
              ...columnDef,
              minWidth: 300,
              maxWidth: 360,
              hide: true
          }
      } else if (el === 'manageCompany') {
          columnDef = {
              ...columnDef,
              minWidth: 300,
              maxWidth: 360 
          }
      } 
      return columnDef;
  })

  if (isLoading) {
      return <div className='loading-wrap'><Loader/></div>
  }

  return (
    <div>
      <Select
          className={""}
          styles={selectStyles}
          options={selectUsersList
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(item => ({value: item.id, label: item.name}))}
          isSearchable={true}
          defaultValue={{value: selectedUser.id, label: selectedUser.name}}
          name={"user"}
          placeholder={"User"}
          components={{
              IndicatorSeparator: () => null
          }}
          onChange={(option) => handleChangeSelectedUser(option)}
      />
      {    
        <PreTableManageBlogs
          rowData={blogsList}
          columnDefs={columnDefs}
          setColumnDefs={setColumnDefs}
          addPopupStatus={addPopupStatus}
          setAddPopupStatus={setAddPopupStatus}
          createPopupStatus={createPopupStatus}
          setCreatePopupStatus={setCreatePopupStatus}
          initialValues={initialValues}
          setInitialValues={setInitialValues}
          modifiedColumnsData={modifiedColumnsData}
          addPopupWithConfigData={addPopupWithConfigData}
          isPopupType={isPopupType}
          setPopupType={setPopupType}
          // remarksList={remarksList}
          setDeletePopupStatus={setDeletePopupStatus}
          deletePopupStatus={deletePopupStatus}
          isDeleteButton={true}
          setIsTriggerUpdate={setIsTriggerUpdate}
          setBlogsList={setBlogsList}
          projectsList={projectsList}
          usersList={usersList}
          manageCompaniesIdList={manageCompaniesIdList}
          setReload={setReload}
          reload={reload}
          selectedUser={selectedUser}
        /> 
      }
    </div>
  )
}

export default ManageBlogs;